import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { wwPointsEstimate, slimmingWorldSynsEstimate } from "@/lib/dietPoints";
import { loadCache, saveCache } from "@/lib/localCache";
import LogKcalButton from "@/components/LogKcalButton";

export default function Discover() {
  const [tab, setTab] = useState("foryou");
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState(null);
  const [meals, setMeals] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);

  // Personalised "For You" shelf state
  const [forYouMeals, setForYouMeals] = useState([]);
  const [forYouContext, setForYouContext] = useState(null);
  const [forYouReason, setForYouReason] = useState(null);
  const [forYouLoading, setForYouLoading] = useState(false);
  const [forYouLoaded, setForYouLoaded] = useState(false);

  const [query, setQuery] = useState("");
  // Preset filter chips — one-tap toggles that get composed into the AI search query.
  const [diet, setDiet] = useState(null);           // single-choice: keto / high-protein / low-cal / etc.
  const [freeFrom, setFreeFrom] = useState({});     // multi-select allergens (gluten-free etc)
  const [milk, setMilk] = useState({});         // multi-select milk types
  const [maxPts, setMaxPts] = useState(0);          // Max SW syns or WW pts (0 = no limit)
  const [minProtein, setMinProtein] = useState(0);  // Min protein per serving in grams (0 = any)
  const [maxFat, setMaxFat] = useState(0);          // Max fat per serving in grams (0 = any)
  const [aiMeals, setAiMeals] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingMore, setAiLoadingMore] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState({});
  const [addingId, setAddingId] = useState(null);
  const [addedIds, setAddedIds] = useState({});
  const [expandedCache, setExpandedCache] = useState({}); // temp_id -> full meal
  const [similarLoadingId, setSimilarLoadingId] = useState(null);

  // Detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    // Categories rarely change — cache for 24h so the "Browse curated" tab loads instantly.
    const cachedCats = loadCache("discover_cats_v1", 24 * 60 * 60 * 1000);
    if (Array.isArray(cachedCats) && cachedCats.length) {
      setCats(cachedCats);
      if (cachedCats[0]) loadCategory(cachedCats[0].id);
    }
    (async () => {
      try {
        const { data } = await api.get("/healthy-meals/categories");
        setCats(data);
        saveCache("discover_cats_v1", data);
        if (!cachedCats?.length && data[0]) loadCategory(data[0].id);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadForYou = async () => {
    setForYouLoading(true);
    try {
      const { data } = await api.get("/healthy-meals/for-you", { timeout: 35000 });
      if (data.personalised === false) {
        setForYouReason(data.reason || "no_profile");
        setForYouMeals([]);
        setForYouContext(null);
      } else {
        setForYouReason(null);
        setForYouContext(data.context || null);
        setForYouMeals(data.meals || []);
      }
      setForYouLoaded(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "For-You shelf timed out — try again");
    } finally {
      setForYouLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "foryou" && !forYouLoaded && !forYouLoading) {
      loadForYou();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadCategory = async (id) => {
    setCategory(id);
    setBrowseLoading(true);
    setMeals([]);
    try {
      const { data } = await api.get(`/healthy-meals/browse?category=${encodeURIComponent(id)}`);
      setMeals(data);
    } catch {
      toast.error("Could not load meals");
    } finally {
      setBrowseLoading(false);
    }
  };

  // If the user switches away from SW/WW, drop the points cap so it doesn't linger.
  const isSW = diet && /slimming world/i.test(diet);
  const isWW = diet && /weight watchers/i.test(diet);
  const pointsLabel = isSW ? "syns" : isWW ? "pts" : "pts";
  const showPointsSlider = isSW || isWW;
  useEffect(() => {
    if (!showPointsSlider && maxPts !== 0) setMaxPts(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diet]);

  const runAiSearch = async (e, override) => {
    e?.preventDefault?.();
    // Compose the effective query from any override + user-typed text + selected chips.
    const baseText = (override ?? query).trim();
    const dietText = diet ? `${diet}` : "";
    const freeFromText = Object.keys(freeFrom).filter((k) => freeFrom[k]).join(", ");
    const milkKeys = Object.keys(milk).filter((k) => milk[k]);
    const milkText = milkKeys.length ? `use ${milkKeys.join(" or ")}` : "";
    const ptsText = (maxPts > 0 && showPointsSlider)
      ? (isSW ? `under ${maxPts} Slimming World syns per serving` : `under ${maxPts} Weight Watchers points per serving`)
      : "";
    const proteinText = minProtein > 0 ? `at least ${minProtein}g protein per serving` : "";
    const fatText = maxFat > 0 ? `under ${maxFat}g fat per serving` : "";
    const composed = [baseText, dietText, freeFromText && `no ${freeFromText}`, milkText, ptsText, proteinText, fatText]
      .filter(Boolean).join(" · ");
    const q = composed || "healthy balanced meal";
    if (q.length < 2) {
      toast.error("Type at least 2 characters or pick a chip");
      return;
    }
    if (override !== undefined) setQuery(override);
    setTab("ai");
    setAiLoading(true);
    setAiMeals([]);
    try {
      const { data } = await api.post("/healthy-meals/search", { query: q }, { timeout: 35000 });
      let meals = data.meals || [];
      // Client-side filter — AI may overshoot the cap. Trim results that don't fit.
      if (maxPts > 0 && showPointsSlider && meals.length) {
        const est = isSW ? slimmingWorldSynsEstimate : wwPointsEstimate;
        meals = meals.filter((m) => {
          const v = est(m);
          return v == null || v <= maxPts;
        });
      }
      setAiMeals(meals);
      setLastSearchQuery(q);
      if (!meals.length) {
        const hint = maxPts > 0 && showPointsSlider ? ` under ${maxPts} ${pointsLabel}` : "";
        toast.error(`No meals${hint} — try relaxing your filters or rephrasing`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "AI is taking too long — please try again in a moment" : "Search failed — try a simpler query");
      toast.error(typeof msg === "string" ? msg : "Search failed — try a simpler query");
    } finally {
      setAiLoading(false);
    }
  };

  // "Load more" — asks the AI for another batch on the same query, telling it
  // which titles we've already shown so the new batch stays fresh and doesn't
  // duplicate anything.
  const loadMoreAi = async () => {
    if (!lastSearchQuery || aiLoadingMore) return;
    const existingTitles = aiMeals.map((m) => m.title).filter(Boolean);
    const seenClause = existingTitles.length
      ? ` — different from: ${existingTitles.slice(-12).join(", ")}`
      : "";
    const nonceClause = ` (batch ${Math.floor(existingTitles.length / 6) + 1})`;
    const moreQ = `${lastSearchQuery}${seenClause}${nonceClause}`.slice(0, 800);
    setAiLoadingMore(true);
    try {
      const seen = new Set(existingTitles.map((t) => t.toLowerCase()));

      // First attempt
      let { data } = await api.post("/healthy-meals/search", { query: moreQ }, { timeout: 35000 });
      let extra = (data.meals || []).filter((m) => m.title && !seen.has(m.title.toLowerCase()));

      // If everything came back as a duplicate, try once more with a stronger nonce so
      // the 24h cache doesn't return the same batch.
      if (!extra.length) {
        const retryQ = `${lastSearchQuery} — new ideas, variety batch ${Date.now() % 100000}${seenClause}`.slice(0, 800);
        const { data: data2 } = await api.post("/healthy-meals/search", { query: retryQ }, { timeout: 35000 });
        extra = (data2.meals || []).filter((m) => m.title && !seen.has(m.title.toLowerCase()));
      }

      // Also respect the SW/WW cap client-side, same as the initial search.
      if (maxPts > 0 && showPointsSlider && extra.length) {
        const est = isSW ? slimmingWorldSynsEstimate : wwPointsEstimate;
        extra = extra.filter((m) => {
          const v = est(m);
          return v == null || v <= maxPts;
        });
      }
      if (!extra.length) {
        toast.error("No fresh meals — try tweaking a filter or search term");
        return;
      }
      setAiMeals((cur) => [...cur, ...extra]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Load more failed — try again";
      toast.error(typeof msg === "string" ? msg : "Load more failed — try again");
    } finally {
      setAiLoadingMore(false);
    }
  };

  const openMealDb = async (meal_id) => {
    setDetail({ _placeholder: true, meal_id });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/healthy-meals/meal/${meal_id}`);
      setDetail({ ...data, _source: "mealdb" });
    } catch {
      toast.error("Could not load meal");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAi = async (meal) => {
    // Show modal with sketch instantly, then lazy-fetch full details
    if (expandedCache[meal.temp_id]) {
      setDetail({ ...meal, ...expandedCache[meal.temp_id], _source: "ai" });
      return;
    }
    setDetail({ ...meal, _source: "ai" });
    setDetailLoading(true);
    try {
      const { data } = await api.post("/healthy-meals/expand", { title: meal.title, blurb: meal.blurb }, { timeout: 35000 });
      setExpandedCache((prev) => ({ ...prev, [meal.temp_id]: data }));
      setDetail((prev) => prev && prev.temp_id === meal.temp_id ? { ...prev, ...data, _source: "ai" } : prev);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "Recipe details are taking too long — please try again" : "Could not load recipe details");
      toast.error(typeof msg === "string" ? msg : "Could not load recipe details");
    } finally {
      setDetailLoading(false);
    }
  };

  // Ensure we have full ingredients/steps before save/add. Returns the
  // hydrated meal payload (sketch + expansion).
  const hydrateMeal = async (meal) => {
    if (meal.ingredients && meal.ingredients.length) return meal;
    const cached = expandedCache[meal.temp_id];
    if (cached) return { ...meal, ...cached };
    const { data } = await api.post("/healthy-meals/expand", { title: meal.title, blurb: meal.blurb }, { timeout: 35000 });
    setExpandedCache((prev) => ({ ...prev, [meal.temp_id]: data }));
    return { ...meal, ...data };
  };

  const saveMeal = async (meal) => {
    setSavingId(meal.temp_id || meal.meal_id || meal.title);
    try {
      const full = await hydrateMeal(meal);
      const payload = {
        title: full.title,
        image: full.image || null,
        source_url: full.source_url || null,
        servings: full.servings || 2,
        ingredients: full.ingredients || [],
        steps: full.steps || [],
        tags: full.tags || [],
        calories_per_serving: full.calories_per_serving ?? null,
        protein_g: full.protein_g ?? null,
        carbs_g: full.carbs_g ?? null,
        fat_g: full.fat_g ?? null,
      };
      const { data } = await api.post("/healthy-meals/save", payload);
      setSavedIds((prev) => ({ ...prev, [meal.temp_id || meal.meal_id || meal.title]: data.recipe_id }));
      toast.success("Saved to your recipes");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const addToBasket = async (meal) => {
    const id = meal.temp_id || meal.meal_id || meal.title;
    setAddingId(id);
    try {
      const full = await hydrateMeal(meal);
      if (!full.ingredients || full.ingredients.length === 0) {
        toast.error("This meal has no ingredients yet");
        return;
      }
      const payload = {
        meal: {
          title: full.title,
          image: full.image || null,
          source_url: full.source_url || null,
          servings: full.servings || 2,
          ingredients: full.ingredients,
          steps: full.steps || [],
          tags: full.tags || [],
          calories_per_serving: full.calories_per_serving ?? null,
        },
      };
      const { data } = await api.post("/shopping-lists/add-meal", payload);
      setAddedIds((prev) => ({ ...prev, [id]: data.list.list_id }));
      const verb = data.created ? "Created basket" : "Added to";
      toast.success(`${verb} "${data.list.name}" · +${data.added_count} ingredients`, {
        duration: 6000,
        action: { label: "View list", onClick: () => { window.location.href = `/app/shopping/${data.list.list_id}`; } },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to basket");
    } finally {
      setAddingId(null);
    }
  };

  const moreLikeThis = async (meal) => {
    setSimilarLoadingId(meal.temp_id);
    try {
      const { data } = await api.post("/healthy-meals/similar", {
        title: meal.title,
        blurb: meal.blurb,
      });
      const newMeals = data.meals || [];
      if (newMeals.length === 0) {
        toast("No similar meals found");
        return;
      }
      setAiMeals((prev) => {
        const idx = prev.findIndex((m) => m.temp_id === meal.temp_id);
        if (idx === -1) return [...prev, ...newMeals];
        return [...prev.slice(0, idx + 1), ...newMeals, ...prev.slice(idx + 1)];
      });
      toast.success(`+${newMeals.length} similar ideas added below`);
    } catch {
      toast.error("Could not fetch more like this");
    } finally {
      setSimilarLoadingId(null);
    }
  };

  return (
    <div className="space-y-8 ff-fade-in">
      <header>
        <div className="ff-label">What to eat</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Find healthy meals fast
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-xl">
          Type what you fancy or browse curated categories. Save anything to your recipe library in one tap — and we&apos;ll line up the shopping list for you.
        </p>
      </header>

      <div className="inline-flex bg-[#F3F0EA] rounded-full p-1" data-testid="discover-tabs">
        <button
          onClick={() => setTab("foryou")}
          data-testid="discover-tab-foryou"
          className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === "foryou" ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"}`}
        >🎯 For you</button>
        <button
          onClick={() => setTab("ai")}
          data-testid="discover-tab-ai"
          className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === "ai" ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"}`}
        >✨ AI search</button>
        <button
          onClick={() => setTab("browse")}
          data-testid="discover-tab-browse"
          className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === "browse" ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"}`}
        >🥗 Browse curated</button>
      </div>

      {tab === "foryou" && (
        <section data-testid="foryou-section">
          {forYouLoading && (
            <div className="ff-card p-8 mt-2 text-center" data-testid="foryou-loading">
              <div className="text-2xl">🎯</div>
              <h3 className="text-lg font-medium mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Building your personalised shelf…
              </h3>
              <p className="text-sm text-[#5C6B61] mt-2">Matching your remaining calories & macros for today.</p>
            </div>
          )}

          {!forYouLoading && forYouReason === "no_profile" && (
            <div className="ff-card p-8 mt-2 text-center" data-testid="foryou-no-profile">
              <div className="text-3xl">🎯</div>
              <h3 className="text-xl font-semibold mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Personalised meals unlock once you set a daily target
              </h3>
              <p className="text-sm text-[#5C6B61] mt-2 max-w-md mx-auto">
                Head to <Link to="/app/calories" className="text-[#3B5A3E] underline font-medium">Calories</Link>, tell us your age / weight / goal, and we&apos;ll tailor every meal here to what you have left to eat today.
              </p>
            </div>
          )}

          {!forYouLoading && forYouContext && (
            <div className="ff-card p-5 md:p-6 mt-2 bg-gradient-to-br from-[#EDF3E9] to-[#E5E2DA]" data-testid="foryou-context">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="ff-label text-[#3B5A3E]">Today&apos;s pick · {forYouContext.meal_slot}</div>
                  <h3 className="text-lg sm:text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {forYouContext.remaining_kcal > 0
                      ? `You have ${forYouContext.remaining_kcal} kcal left today`
                      : "You've hit your kcal target — try a light snack"}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="ff-chip" style={{ background: "#F6D6CE", color: "#A33D26" }}>
                      🥩 {forYouContext.remaining_protein_g}g protein left
                    </span>
                    <span className="ff-chip" style={{ background: "#FBE9C4", color: "#7A5A11" }}>
                      🌾 {forYouContext.remaining_carbs_g}g carbs left
                    </span>
                    <span className="ff-chip" style={{ background: "#D9E1EE", color: "#3E4E68" }}>
                      🥑 {forYouContext.remaining_fat_g}g fat left
                    </span>
                    {forYouContext.diet_pref && forYouContext.diet_pref !== "any" && (
                      <span className="ff-chip">{forYouContext.diet_pref}</span>
                    )}
                    {forYouContext.avoid_pref && (
                      <span className="ff-chip">no {forYouContext.avoid_pref}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setForYouLoaded(false); loadForYou(); }}
                  data-testid="foryou-refresh"
                  className="shrink-0 text-xs font-medium rounded-full px-3 py-2 bg-white hover:bg-[#F3F0EA] transition"
                  title="Refresh personalised shelf"
                >
                  ↻ Refresh
                </button>
              </div>
            </div>
          )}

          {!forYouLoading && forYouMeals.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="foryou-grid">
              {forYouMeals.map((m) => (
                <MealCard
                  key={m.temp_id}
                  meal={m}
                  diet={diet}
                  onOpen={() => openAi(m)}
                  onSave={() => saveMeal(m)}
                  onAdd={() => addToBasket(m)}
                  onMoreLikeThis={() => moreLikeThis(m)}
                  similarLoading={similarLoadingId === m.temp_id}
                  saving={savingId === m.temp_id}
                  saved={!!savedIds[m.temp_id]}
                  adding={addingId === m.temp_id}
                  added={!!addedIds[m.temp_id]}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "ai" && (
        <section>
          {/* Big "Healthy" quick-search button — one tap for a solid balanced meal. */}
          <div className="mb-3">
            <button
              type="button"
              data-testid="quick-healthy-btn"
              onClick={(e) => runAiSearch(e, "healthy balanced meal, plenty of veg, lean protein")}
              disabled={aiLoading}
              className="w-full rounded-2xl px-4 py-3 flex items-center gap-3 hover:shadow-md transition disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#E8F0E9 0%,#C8D8CA 100%)", border: "1px solid #A6C0AA" }}
            >
              <span className="text-2xl" aria-hidden>🥗</span>
              <div className="flex-1 text-left">
                <div className="text-[15px] font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Find me something healthy
                </div>
                <div className="text-xs text-[#3B5A3E]">Balanced, veg-forward · one tap, no typing</div>
              </div>
              <span className="text-[#3B5A3E] font-semibold text-sm">Search →</span>
            </button>
          </div>

          <form onSubmit={runAiSearch} className="flex flex-col sm:flex-row gap-2" data-testid="ai-search-form">
            <input
              data-testid="ai-search-input"
              className="ff-input flex-1"
              placeholder="Type anything — chips below add filters automatically"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              maxLength={200}
            />
            <button type="submit" disabled={aiLoading} data-testid="ai-search-btn" className="ff-btn-primary disabled:opacity-60 whitespace-nowrap">
              {aiLoading ? "Cooking up…" : "Search"}
            </button>
          </form>

          {/* Filter chip groups. Diet + Milk are single-select radios; Free-from are multi-select checkboxes. */}
          <div className="mt-4 space-y-3" data-testid="discover-filters">
            <ChipGroup
              label="Diet style"
              testid="diet-chips"
              value={diet}
              onChange={setDiet}
              options={[
                { id: "high protein", label: "💪 High protein" },
                { id: "keto (very low carb)", label: "🥩 Keto" },
                { id: "Atkins low-carb", label: "🍳 Atkins" },
                { id: "carnivore (meat, fish, eggs, dairy only)", label: "🥩 Carnivore" },
                { id: "low calorie (under 500 kcal per serving)", label: "🍎 Low calorie" },
                { id: "Slimming World friendly (Free foods, minimal syns)", label: "⭐ Slimming World" },
                { id: "Weight Watchers friendly (low points)", label: "🎯 Weight Watchers" },
                { id: "intermittent fasting friendly (satisfying within a smaller eating window)", label: "⏱️ Fasting-friendly" },
                { id: "Mediterranean", label: "🥑 Mediterranean" },
                { id: "vegetarian", label: "🥦 Vegetarian" },
                { id: "vegan", label: "🌱 Vegan" },
                { id: "pescatarian", label: "🐟 Pescatarian" },
                { id: "gluten-free", label: "🌾 Gluten-free" },
              ]}
            />

            <ChipGroup
              label="Free-from / allergies"
              testid="free-from-chips"
              multi
              value={freeFrom}
              onChange={setFreeFrom}
              options={[
                { id: "gluten", label: "Gluten" },
                { id: "dairy", label: "Dairy" },
                { id: "nuts", label: "Nuts" },
                { id: "peanuts", label: "Peanuts" },
                { id: "eggs", label: "Eggs" },
                { id: "soy", label: "Soy" },
                { id: "sesame", label: "Sesame" },
                { id: "fish", label: "Fish" },
                { id: "shellfish", label: "Shellfish" },
                { id: "pork", label: "Pork" },
                { id: "beef", label: "Beef" },
                { id: "alcohol", label: "Alcohol" },
              ]}
            />

            <ChipGroup
              label="Milk (if the recipe uses milk)"
              testid="milk-chips"
              multi
              value={milk}
              onChange={setMilk}
              options={[
                { id: "oat milk", label: "🌾 Oat" },
                { id: "almond milk", label: "🌰 Almond" },
                { id: "soy milk", label: "🌱 Soy" },
                { id: "coconut milk", label: "🥥 Coconut" },
                { id: "lactose-free cow's milk", label: "🥛 Lactose-free" },
                { id: "whole cow's milk", label: "🐄 Whole cow's" },
              ]}
            />

            {showPointsSlider && (
              <div data-testid="max-points-slider">
                <div className="ff-label mb-1.5 flex items-center justify-between">
                  <span>Max {isSW ? "Slimming World syns" : "WW points"} per serving</span>
                  <span className="text-[#3B5A3E] font-semibold">
                    {maxPts === 0 ? "No limit" : `≤ ${maxPts} ${pointsLabel}`}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={isSW ? 30 : 20}
                  step={1}
                  value={maxPts}
                  onChange={(e) => setMaxPts(Number(e.target.value))}
                  data-testid="max-points-range"
                  className="w-full accent-[#3B5A3E]"
                  aria-label={`Max ${pointsLabel} per serving`}
                />
                <p className="text-[10px] text-[#8B9690] mt-1 leading-relaxed">
                  Filters returned meals to fit within your daily allowance. Estimates only.
                </p>
              </div>
            )}

            <div data-testid="macro-sliders" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div data-testid="min-protein-slider">
                <div className="ff-label mb-1.5 flex items-center justify-between">
                  <span>💪 Min protein per serving</span>
                  <span className="text-[#3B5A3E] font-semibold">
                    {minProtein === 0 ? "Any" : `≥ ${minProtein}g`}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={5}
                  value={minProtein}
                  onChange={(e) => setMinProtein(Number(e.target.value))}
                  data-testid="min-protein-range"
                  className="w-full accent-[#3B5A3E]"
                  aria-label="Min protein per serving in grams"
                />
              </div>
              <div data-testid="max-fat-slider">
                <div className="ff-label mb-1.5 flex items-center justify-between">
                  <span>🥑 Max fat per serving</span>
                  <span className="text-[#3B5A3E] font-semibold">
                    {maxFat === 0 ? "Any" : `≤ ${maxFat}g`}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={5}
                  value={maxFat}
                  onChange={(e) => setMaxFat(Number(e.target.value))}
                  data-testid="max-fat-range"
                  className="w-full accent-[#3B5A3E]"
                  aria-label="Max fat per serving in grams"
                />
              </div>
            </div>

            {(diet || Object.values(milk).some(Boolean) || Object.values(freeFrom).some(Boolean) || maxPts > 0 || minProtein > 0 || maxFat > 0) && (
              <button
                type="button"
                onClick={() => { setDiet(null); setMilk({}); setFreeFrom({}); setMaxPts(0); setMinProtein(0); setMaxFat(0); }}
                data-testid="clear-filters-btn"
                className="text-xs text-[#5C6B61] hover:text-[#3B5A3E] underline underline-offset-2"
              >
                Clear all filters
              </button>
            )}
          </div>

          {aiLoading && (
            <div className="ff-card p-8 mt-6 text-center" data-testid="ai-loading-card">
              <div className="text-5xl mb-3 animate-pulse">🍳</div>
              <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Cooking up 10 ideas…</h3>
              <p className="text-sm text-[#5C6B61] mt-2">Should be ready in about 10 seconds. Full ingredients load when you open a recipe.</p>
              <div className="mt-5 h-1.5 bg-[#F3F0EA] rounded-full overflow-hidden max-w-xs mx-auto">
                <div className="h-full bg-[#3B5A3E] animate-pulse" style={{ width: "60%" }} />
              </div>
            </div>
          )}
          {!aiLoading && aiMeals.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="ai-results-grid">
              {aiMeals.map((m) => (
                <MealCard
                  key={m.temp_id}
                  meal={m}
                  diet={diet}
                  onOpen={() => openAi(m)}
                  onSave={() => saveMeal(m)}
                  onAdd={() => addToBasket(m)}
                  onMoreLikeThis={() => moreLikeThis(m)}
                  similarLoading={similarLoadingId === m.temp_id}
                  saving={savingId === m.temp_id}
                  saved={!!savedIds[m.temp_id]}
                  adding={addingId === m.temp_id}
                  added={!!addedIds[m.temp_id]}
                />
              ))}
            </div>
          )}
          {aiMeals.length > 0 && !aiLoading && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-testid="load-more-wrap">
              <button
                type="button"
                onClick={loadMoreAi}
                disabled={aiLoadingMore}
                data-testid="load-more-btn"
                className="text-sm font-semibold rounded-full px-6 py-3 bg-[#3B5A3E] text-white hover:bg-[#2A4230] transition disabled:opacity-60 shadow-sm"
              >
                {aiLoadingMore ? "Finding more…" : `✨ Load ${aiMeals.length > 0 ? "more" : ""} meals`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAiMeals([]);
                  const base = lastSearchQuery || query || "healthy balanced meal";
                  // Append a fresh nonce so the 24h cache doesn't return the same batch.
                  const fresh = `${base} — fresh batch ${Date.now() % 100000}`;
                  runAiSearch({ preventDefault: () => {} }, fresh);
                }}
                disabled={aiLoading || aiLoadingMore}
                data-testid="reset-search-btn"
                title="Start over with a completely fresh set of 10 meals"
                className="text-sm font-medium rounded-full px-5 py-3 bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA] transition disabled:opacity-60"
              >
                🔄 Reset & search again
              </button>
            </div>
          )}
        </section>
      )}

      {tab === "browse" && (
        <section>
          <div className="flex flex-wrap gap-2" data-testid="category-picker">
            {cats.map((c) => (
              <button
                key={c.id}
                onClick={() => loadCategory(c.id)}
                data-testid={`category-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition ${category === c.id ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
              >
                <span className="mr-1">{c.emoji}</span>{c.label}
              </button>
            ))}
          </div>

          {browseLoading && <div className="ff-card p-8 mt-6 text-[#5C6B61]">Loading meals…</div>}
          {!browseLoading && meals.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="browse-results-grid">
              {meals.map((m) => (
                <BrowseCard key={m.meal_id} meal={m} onOpen={() => openMealDb(m.meal_id)} />
              ))}
            </div>
          )}
        </section>
      )}

      {(detail || detailLoading) && (
        <DetailModal
          detail={detail && !detail._placeholder ? detail : null}
          loading={detailLoading}
          onClose={() => { setDetail(null); setDetailLoading(false); }}
          onSave={(d) => saveMeal(d)}
          onAdd={(d) => addToBasket(d)}
          savingId={savingId}
          savedIds={savedIds}
          addingId={addingId}
          addedIds={addedIds}
        />
      )}
    </div>
  );
}

function MealCard({ meal, onOpen, onSave, onAdd, onMoreLikeThis, similarLoading, saving, saved, adding, added, diet }) {
  // Show SW syns / WW points estimates ONLY when the user has picked that diet
  // from the chip filter — keeps regular searches uncluttered.
  const showWW = diet && /weight watchers/i.test(diet);
  const showSW = diet && /slimming world/i.test(diet);
  const wwPts = showWW ? wwPointsEstimate(meal) : null;
  const swSyns = showSW ? slimmingWorldSynsEstimate(meal) : null;
  return (
    <div className="ff-card ff-card-hover overflow-hidden flex flex-col" data-testid="ai-meal-card">
      {meal.image && (
        <button onClick={onOpen} className="block w-full h-40 bg-[#F3F0EA] overflow-hidden" data-testid="meal-card-image-btn">
          <img
            src={meal.image}
            alt={meal.title}
            loading="lazy"
            className="w-full h-full object-cover hover:scale-105 transition"
          />
        </button>
      )}
      <div className="p-5 flex flex-col flex-1">
      {meal.similar_to && (
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690] mb-2" data-testid="similar-to-tag">
          📌 like &ldquo;{meal.similar_to}&rdquo;
        </div>
      )}
      <button onClick={onOpen} className="text-left flex-1">
        <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{meal.title}</h3>
        {meal.blurb && <p className="text-sm text-[#5C6B61] mt-1">{meal.blurb}</p>}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {meal.calories_per_serving && (
            <span className="ff-chip" style={{ background: "#E8F0E9", color: "#3B5A3E" }}>{meal.calories_per_serving} kcal</span>
          )}
          {wwPts != null && (
            <span
              className="ff-chip"
              style={{ background: "#EAF3FF", color: "#1F4E8C" }}
              title="Approximate Weight Watchers points — estimated from kcal/protein/fat, not official"
              data-testid="ww-points-chip"
            >
              ~{wwPts} pts
            </span>
          )}
          {swSyns != null && (
            <span
              className="ff-chip"
              style={{ background: "#FFF1DB", color: "#8A5A00" }}
              title="Approximate Slimming World syns — estimated from fat content, not official"
              data-testid="sw-syns-chip"
            >
              ~{swSyns} syns
            </span>
          )}
          {(meal.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="ff-chip">{t}</span>
          ))}
        </div>
        {(showWW || showSW) && (
          <p className="text-[10px] text-[#8B9690] mt-2 leading-relaxed" data-testid="points-disclaimer">
            Estimates only — not affiliated with, endorsed by, or licensed by Slimming World® or WW®. Always check with your group.
          </p>
        )}
      </button>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          onClick={onSave}
          disabled={saving || saved}
          data-testid="save-ai-meal-btn"
          className={`text-sm rounded-full px-3 py-2 font-medium transition border ${saved ? "bg-[#E8F0E9] text-[#3B5A3E] border-[#E8F0E9]" : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
        >
          {saved ? "✓ Saved" : saving ? "…" : "Save"}
        </button>
        <button
          onClick={onAdd}
          disabled={adding || added}
          data-testid="add-to-basket-btn"
          className={`text-sm rounded-full px-3 py-2 font-medium transition ${added ? "bg-[#E8F0E9] text-[#3B5A3E]" : "bg-[#3B5A3E] text-white hover:bg-[#2c4530]"}`}
        >
          {added ? "✓ In basket" : adding ? "Adding…" : "🛒 Add to list"}
        </button>
      </div>
      <button
        onClick={onMoreLikeThis}
        disabled={similarLoading}
        data-testid="more-like-this-btn"
        className="mt-2 text-xs font-medium text-[#5C6B61] hover:text-[#3B5A3E] py-1.5 rounded-full hover:bg-[#F3F0EA] transition disabled:opacity-60"
      >
        {similarLoading ? "Finding similar…" : "📌 More like this →"}
      </button>
      </div>
    </div>
  );
}

function BrowseCard({ meal, onOpen }) {
  return (
    <button onClick={onOpen} className="ff-card ff-card-hover overflow-hidden text-left" data-testid="browse-meal-card">
      <div className="aspect-video bg-[#F3F0EA] overflow-hidden">
        {meal.image && <img src={meal.image} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="p-4">
        <h3 className="text-base font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{meal.title}</h3>
      </div>
    </button>
  );
}

function DetailModal({ detail, loading, onClose, onSave, onAdd, savingId, savedIds, addingId, addedIds }) {
  const keyId = detail?.temp_id || detail?.meal_id || detail?.title;
  const isSaved = !!savedIds[keyId];
  const isSaving = savingId === keyId;
  const isAdded = !!addedIds[keyId];
  const isAdding = addingId === keyId;

  // Lock body scroll & support Escape to close while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-40 bg-[#2A332C]/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="meal-detail-modal"
    >
      <div
        className="ff-card w-full max-w-2xl p-6 md:p-8 max-h-[85vh] max-h-[85dvh] overflow-y-auto overscroll-contain relative my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="close-meal-detail-x"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F3F0EA] hover:bg-[#E5E2DA] flex items-center justify-center text-[#2A332C] text-lg"
        >
          ✕
        </button>
        {!detail && loading && (
          <div className="py-8 flex flex-col items-center gap-3 text-center" data-testid="meal-detail-loading">
            <div className="w-9 h-9 rounded-full border-2 border-[#E5E2DA] border-t-[#3B5A3E] animate-spin" />
            <div className="text-[#5C6B61]">Loading recipe…</div>
          </div>
        )}
        {!detail && !loading && (
          <div className="py-8 text-center" data-testid="meal-detail-empty">
            <p className="text-[#5C6B61]">Could not load this recipe. Pull down to refresh, or pick another meal.</p>
            <button onClick={onClose} className="ff-btn-ghost mt-4">Close</button>
          </div>
        )}
        {detail && (
          <>
            {detail.image && <img src={detail.image} alt="" className="w-full aspect-video object-cover rounded-2xl mb-4" />}
            <div className="ff-label">{detail._source === "ai" ? "AI suggestion" : detail.category || "Curated"}</div>
            <h2 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{detail.title}</h2>
            {detail.blurb && <p className="text-sm text-[#5C6B61] mt-2">{detail.blurb}</p>}

            <div className="flex flex-wrap gap-2 mt-3">
              {detail.calories_per_serving && <span className="ff-chip" style={{ background: "#E8F0E9", color: "#3B5A3E" }}>{detail.calories_per_serving} kcal / serving</span>}
              {detail.protein_g && <span className="ff-chip">{Math.round(detail.protein_g)}g protein</span>}
              {(() => {
                const ww = wwPointsEstimate(detail);
                return ww != null ? (
                  <span
                    className="ff-chip"
                    style={{ background: "#EAF3FF", color: "#1F4E8C" }}
                    title="Approximate Weight Watchers points"
                    data-testid="detail-ww-points-chip"
                  >~{ww} WW pts</span>
                ) : null;
              })()}
              {(() => {
                const sw = slimmingWorldSynsEstimate(detail);
                return sw != null ? (
                  <span
                    className="ff-chip"
                    style={{ background: "#FFF1DB", color: "#8A5A00" }}
                    title="Approximate Slimming World syns"
                    data-testid="detail-sw-syns-chip"
                  >~{sw} SW syns</span>
                ) : null;
              })()}
              {(detail.tags || []).slice(0, 5).map((t) => <span key={t} className="ff-chip">{t}</span>)}
            </div>
            <p className="text-[10px] text-[#8B9690] mt-1">
              Points/syns are unofficial estimates — not affiliated with Slimming World® or WW®. Always check with your group.
            </p>

            {loading && (!detail.ingredients || detail.ingredients.length === 0) ? (
              <div className="mt-6 rounded-2xl bg-[#F3F0EA] p-6 text-center" data-testid="detail-loading">
                <div className="animate-pulse text-2xl mb-2">🍳</div>
                <p className="text-sm text-[#5C6B61]">Loading full ingredients &amp; method… (~5–10 seconds)</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <div className="ff-label mb-2">Ingredients</div>
                  <ul className="space-y-1.5 text-sm">
                    {(detail.ingredients || []).map((i, k) => (
                      <li key={k} className="flex items-start gap-2">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3B5A3E] flex-none" />
                        <span><strong>{i.quantity ? `${i.quantity} ` : ""}</strong>{i.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="ff-label mb-2">Method</div>
                  <ol className="space-y-2 text-sm list-decimal list-inside">
                    {(detail.steps || []).map((s, k) => <li key={k}>{s}</li>)}
                  </ol>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button onClick={onClose} className="ff-btn-ghost" data-testid="close-meal-detail-btn">Close</button>
              {detail.calories_per_serving != null && (
                <LogKcalButton
                  kcal={detail.calories_per_serving}
                  name={detail.title}
                  source="manual"
                  testid="log-meal-detail-btn"
                  className="rounded-full px-5 py-2.5 text-sm font-medium bg-[#2A332C] text-white hover:bg-[#1d2521] transition disabled:opacity-60"
                />
              )}
              {isSaved ? (
                <Link to="/app/recipes" className="ff-btn-ghost border border-[#E5E2DA] inline-block">In your recipes →</Link>
              ) : (
                <button
                  onClick={() => onSave(detail)}
                  disabled={isSaving}
                  data-testid="save-meal-detail-btn"
                  className="ff-btn-ghost border border-[#E5E2DA] disabled:opacity-60"
                >
                  {isSaving ? "Saving…" : "Save to recipes"}
                </button>
              )}
              <button
                onClick={() => onAdd(detail)}
                disabled={isAdding || isAdded}
                data-testid="add-to-basket-detail-btn"
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition ${isAdded ? "bg-[#E8F0E9] text-[#3B5A3E]" : "bg-[#3B5A3E] text-white hover:bg-[#2c4530]"} disabled:opacity-60`}
              >
                {isAdded ? "✓ In your basket" : isAdding ? "Adding…" : "🛒 Add to shopping list"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Reusable filter chip group.
 *  - Single-select (default): `value` is the id string (or null), `onChange(newId)` sets it.
 *  - Multi-select (`multi` prop): `value` is a map { id: bool }, `onChange(newMap)` sets it.
 */
function ChipGroup({ label, testid, options, value, onChange, multi = false }) {
  const isActive = (id) => (multi ? !!value?.[id] : value === id);
  const toggle = (id) => {
    if (multi) {
      const next = { ...(value || {}) };
      if (next[id]) delete next[id]; else next[id] = true;
      onChange(next);
    } else {
      onChange(value === id ? null : id);
    }
  };
  return (
    <div data-testid={testid}>
      <div className="ff-label mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = isActive(o.id);
          return (
            <button
              type="button"
              key={o.id}
              onClick={() => toggle(o.id)}
              data-testid={`${testid}-${o.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                active
                  ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                  : "bg-white text-[#2A332C] border-[#DADCD3] hover:border-[#3B5A3E] hover:text-[#3B5A3E]"
              }`}
            >
              {active ? "✓ " : "+ "}{o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

